import { OfflineIndicator } from "@/components/pwa/OfflineIndicator";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import cookies from "js-cookie";
import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./components/ui/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import firebaseConfig from "./utils/firebase";

/* =========================
   🔥 LAZY LOAD PAGES
========================= */

const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyOTP = lazy(() => import("./pages/VerifyOTP"));
const PublicScan = lazy(() => import("./pages/PublicScan"));
const PublicAnonymousMessage = lazy(
  () => import("./pages/PublicAnonymousMessage"),
);
const AnonymousMessage = lazy(() => import("./pages/AnonymousMessage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthSuccess = lazy(() => import("./pages/AuthSuccess"));
const CreateProfile = lazy(() => import("./pages/CreateProfile"));

// Dashboard
const Dashboard = lazy(() => import("./pages/Dashboard"));
const QRCodes = lazy(() => import("./pages/dashboard/QRCodes"));
const CreateQRCode = lazy(() => import("./pages/dashboard/CreateQRCode"));
const EditQRCode = lazy(() => import("./pages/dashboard/EditQRCode"));
const MemoryTags = lazy(() => import("./pages/dashboard/MemoryTags"));
const CreateMemoryTags = lazy(
  () => import("./pages/dashboard/CreateMemoryTags"),
);
const EditMemoryTags = lazy(() => import("./pages/dashboard/EditMemoryTags"));
const GenerateMemoryTagQR = lazy(
  () => import("./pages/dashboard/GenerateMemoryTagQR"),
);
const Messages = lazy(() => import("./pages/dashboard/Messages"));
const Notifications = lazy(() => import("./pages/dashboard/Notifications"));
const Settings = lazy(() => import("./pages/dashboard/Settings"));
const SettingsSubscriptionsPage = lazy(
  () => import("./pages/dashboard/settings/SettingsSubscriptionsPage"),
);
const SettingsProfilePage = lazy(
  () => import("./pages/dashboard/settings/SettingsProfilePage"),
);
const SettingsSecurityPage = lazy(
  () => import("./pages/dashboard/settings/SettingsSecurityPage"),
);
const SettingsNotificationsPage = lazy(
  () => import("./pages/dashboard/settings/SettingsNotificationsPage"),
);

// Admin
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminQRCodes = lazy(() => import("./pages/admin/QRCodes"));
const AdminDocuments = lazy(() => import("./pages/admin/Documents"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminNotifications = lazy(() => import("./pages/admin/Notifications"));

/* ========================= */

const app = initializeApp(firebaseConfig);

/* =========================
   🔥 GLOBAL LOADER
========================= */

const Loader = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
    <p className="ml-3 text-muted-foreground">Loading...</p>
  </div>
);

const LazyWrapper = ({ children }) => (
  <Suspense fallback={<Loader />}>{children}</Suspense>
);

/* ========================= */

function App() {
  const showCustomNotification = (payload) => {
    try {
      new Notification(payload.notification?.title || "Notification", {
        body: payload.data?.body || "",
        icon: payload.notification?.image || "/icon.png",
      });
    } catch (err) {
      console.error("Notification error:", err);
    }
  };

  /* =========================
     🔥 FIREBASE MESSAGING
  ========================= */

  useEffect(() => {
    if (typeof window === "undefined") return;

    isSupported().then((supported) => {
      if (!supported || !("Notification" in window)) {
        console.warn("Firebase messaging not supported.");
        return;
      }

      const messaging = getMessaging(app);

      Notification.requestPermission()
        .then((permission) => {
          if (permission !== "granted") return;

          getToken(messaging, {
            vapidKey:
              "BJjnZ0vrFZVWX7ZyZKGw2TR3Z6W_AaOQNUVyGyOh3WOiub0SJVzPAQQu3ttr5y81rn5Lf44YB1cpdI1BTkzGa0Q",
          })
            .then((token) => {
              if (token) {
                console.log("FCM token:", token);
                cookies.set("webToken", token);
              }
            })
            .catch(console.error);

          onMessage(messaging, (payload) => {
            console.log("Foreground message:", payload);
            showCustomNotification(payload);
          });
        })
        .catch(console.error);
    });
  }, []);

  /* =========================
     🔥 SERVICE WORKER
  ========================= */

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration) {
        navigator.serviceWorker
          .register("/firebase-messaging-sw.js")
          .then((reg) => console.log("SW registered:", reg.scope))
          .catch(console.error);
      }
    });
  }, []);

  /* ========================= */

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />

      <AuthProvider>
        <SocketProvider>
          <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/" element={<LazyWrapper><Login /></LazyWrapper>} />
              <Route path="/login" element={<LazyWrapper><Login /></LazyWrapper>} />
              <Route path="/signup" element={<LazyWrapper><Signup /></LazyWrapper>} />
              <Route path="/forgot-password" element={<LazyWrapper><ForgotPassword /></LazyWrapper>} />
              <Route path="/reset-password" element={<LazyWrapper><ResetPassword /></LazyWrapper>} />
              <Route path="/verify-otp" element={<LazyWrapper><VerifyOTP /></LazyWrapper>} />
              <Route path="/scan-details/:id" element={<LazyWrapper><PublicScan /></LazyWrapper>} />
              <Route path="/public-anonymous-message" element={<LazyWrapper><PublicAnonymousMessage /></LazyWrapper>} />
              <Route path="/auth-success" element={<LazyWrapper><AuthSuccess /></LazyWrapper>} />

              {/* Protected */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<LazyWrapper><Dashboard /></LazyWrapper>} />
                <Route path="/create-profile" element={<LazyWrapper><CreateProfile /></LazyWrapper>} />
                <Route path="/dashboard/qr-codes" element={<LazyWrapper><QRCodes /></LazyWrapper>} />
                <Route path="/dashboard/qr-codes/create" element={<LazyWrapper><CreateQRCode /></LazyWrapper>} />
                <Route path="/dashboard/qr-codes/edit" element={<LazyWrapper><EditQRCode /></LazyWrapper>} />
                <Route path="/dashboard/memory-tags" element={<LazyWrapper><MemoryTags /></LazyWrapper>} />
                <Route path="/dashboard/memory-tags/create" element={<LazyWrapper><CreateMemoryTags /></LazyWrapper>} />
                <Route path="/dashboard/memory-tags/edit" element={<LazyWrapper><EditMemoryTags /></LazyWrapper>} />
                <Route path="/dashboard/memory-tags/generate-qr" element={<LazyWrapper><GenerateMemoryTagQR /></LazyWrapper>} />
                <Route path="/dashboard/messages" element={<LazyWrapper><Messages /></LazyWrapper>} />
                <Route path="/dashboard/notifications" element={<LazyWrapper><Notifications /></LazyWrapper>} />
                <Route
                  path="/dashboard/settings"
                  element={
                    <LazyWrapper>
                      <Settings />
                    </LazyWrapper>
                  }
                >
                  <Route
                    index
                    element={<Navigate to="subscriptions" replace />}
                  />
                  <Route
                    path="subscriptions"
                    element={<SettingsSubscriptionsPage />}
                  />
                  <Route path="profile" element={<SettingsProfilePage />} />
                  <Route path="security" element={<SettingsSecurityPage />} />
                  <Route
                    path="notifications"
                    element={<SettingsNotificationsPage />}
                  />
                </Route>
                <Route path="/anonymous-message" element={<LazyWrapper><AnonymousMessage /></LazyWrapper>} />
              </Route>

              {/* Admin */}
              <Route path="/admin" element={<LazyWrapper><Admin /></LazyWrapper>} />
              <Route path="/admin/users" element={<LazyWrapper><AdminUsers /></LazyWrapper>} />
              <Route path="/admin/qr-codes" element={<LazyWrapper><AdminQRCodes /></LazyWrapper>} />
              <Route path="/admin/documents" element={<LazyWrapper><AdminDocuments /></LazyWrapper>} />
              <Route path="/admin/reports" element={<LazyWrapper><AdminReports /></LazyWrapper>} />
              <Route path="/admin/analytics" element={<LazyWrapper><AdminAnalytics /></LazyWrapper>} />
              <Route path="/admin/settings" element={<LazyWrapper><AdminSettings /></LazyWrapper>} />
              <Route path="/admin/notifications" element={<LazyWrapper><AdminNotifications /></LazyWrapper>} />

              {/* Catch */}
              <Route path="*" element={<LazyWrapper><NotFound /></LazyWrapper>} />
            </Routes>

            <OfflineIndicator />
          </BrowserRouter>
        </SocketProvider>
      </AuthProvider>
    </TooltipProvider>
  );
}

export default App;